import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Phone, AlertTriangle, Users, Mail, Search } from 'lucide-react';
import { Input } from './ui/input';
import axios from 'axios';
import { useEffect } from 'react';
import { LoadingSpinner } from './LoadingSpinner';
import DashboardPageLayout from './DashboardPageLayout';

const EmergencyContacts = () => {
  const [team, setTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/teamMembers`);
      setTeam(response.data || []); // Ensure it's always an array
    } catch (error) {
      console.error('Error fetching team:', error);
      setTeam([]); // Set empty array on error
    }
  };

  const filteredTeam = team ? team.filter(member => {
    const matchesSearch = 
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.phone?.includes(searchQuery) ||
      member.username.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  }) : [];

  if (!team) {
    return <LoadingSpinner text="Loading emergency contacts..." />;
  }

  return (
    <DashboardPageLayout
      title="Emergency Contacts"
      subtitle="Quick access to emergency services and team contacts"
    >
      <div className="space-y-6">
      {/* TEAM DIRECTORY - First */}
      <Card className="border-2 border-blue-300 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Users className="w-6 h-6 text-blue-600" />
            📞 Team Directory
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-blue-900 font-semibold mb-4">
            Contact your teamMembers for help, questions, or scheduling:
          </p>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search teamMembers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white"
              maxLength={100}
            />
          </div>

          {/* TeamMembers */}
          {filteredTeam.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No teamMembers found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredTeam.map((member) => (
                <Card key={member.id} className="border-2 border-blue-200 bg-white hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-base truncate">{member.name}</h3>
                        <p className="text-xs text-gray-500">@{member.username}</p>
                      </div>
                    </div>
                    
                    {/* Contact Info */}
                    <div className="space-y-2">
                      {member.email && (
                        <div className="flex items-start gap-2">
                          <Mail className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                          <a 
                            href={`mailto:${member.email}`}
                            className="text-sm text-blue-600 hover:underline break-all"
                          >
                            {member.email}
                          </a>
                        </div>
                      )}

                      {member.phone && (
                        <div className="flex items-start gap-2">
                          <Phone className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                          <a 
                            href={`tel:${member.phone}`}
                            className="text-base text-green-600 hover:underline font-semibold"
                          >
                            {member.phone}
                          </a>
                        </div>
                      )}

                      {!member.email && !member.phone && (
                        <p className="text-sm text-gray-400 italic">No contact info</p>
                      )}
                    </div>

                    {/* Certification Badge */}
                    {member.doubleDeckerCertified && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="text-xs text-green-600 font-medium">✓ Double-Decker Certified</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* EMERGENT - Life-Threatening (Now Second) */}
      <Card className="border-2 border-red-500 bg-red-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-900">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            🚨 Emergency Numbers (Life-Threatening Only)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-900 font-semibold mb-4 text-lg">
            For serious, life-threatening emergencies - CALL IMMEDIATELY:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 911 */}
            <a 
              href="tel:911"
              className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-red-400 hover:border-red-600 hover:shadow-lg transition-all"
            >
              <div className="p-3 bg-red-100 rounded-full">
                <Phone className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="font-bold text-red-900 text-xl">911</p>
                <p className="text-sm text-red-700">Life-Threatening Emergency</p>
              </div>
            </a>

            {/* Lake Austin Fire Department */}
            <a 
              href="tel:+15122662533"
              className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-red-400 hover:border-red-600 hover:shadow-lg transition-all"
            >
              <div className="p-3 bg-red-100 rounded-full">
                <Phone className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="font-bold text-red-900">(512) 266-2533</p>
                <p className="text-sm text-red-700">Lake Austin Fire Dept</p>
              </div>
            </a>

            {/* Flag Ship Towing */}
            <a 
              href="tel:+18882628869"
              className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-orange-300 hover:border-orange-500 hover:shadow-lg transition-all"
            >
              <div className="p-3 bg-orange-100 rounded-full">
                <Phone className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="font-bold text-orange-900">(888) 262-8869</p>
                <p className="text-sm text-orange-700">Flag Ship Towing</p>
              </div>
            </a>

            {/* Texas Parks & Wildlife */}
            <a 
              href="tel:+18007921112"
              className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-green-300 hover:border-green-500 hover:shadow-lg transition-all"
            >
              <div className="p-3 bg-green-100 rounded-full">
                <Phone className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-bold text-green-900">(800) 792-1112</p>
                <p className="text-sm text-green-700">Texas Parks & Wildlife</p>
              </div>
            </a>
          </div>
          <div className="mt-4 p-3 bg-yellow-100 border-l-4 border-yellow-500 rounded-r">
            <p className="text-sm text-yellow-900 font-medium">
              💡 Tap any number to call immediately. Save these contacts in your phone before heading out!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
    </DashboardPageLayout>
  );
};

export default EmergencyContacts;
